import './styles.scss'
import React, { useState, useRef, useEffect } from 'react'
import { Dialog } from 'primereact/dialog'
import { Button } from 'primereact/button'
import { Galleria } from 'primereact/galleria'
import { FileUpload } from 'primereact/fileupload'
import { Toast } from 'primereact/toast'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
import { getLocalUserDetails } from 'Utils/LocalStorageHandler'
import { ButtonComponent } from 'Components/UIComponents'
import apiAdapterCoffeeWeb from 'Services/apiAdapterCoffeeWeb'
import Loader from 'Components/Loader'
import { IMAGE_UPLOAD_PARAMETERS } from 'Utils/Constants'

const ModalComponent = ({ isModalOpen, closeModal, defaultImages, setNewsFeedMedia, newsFeedMedia, setLoading, setButtonDisabled, mockAddNewsFeedModal, mockAddNewsFeedModalApiFlag, mockUploadedFilesWeb, mockUploadedFilesMobile, mockDeviceType, renderCustomUploader, setRenderCustomUploader }) => {
  const galleria = useRef(null)
  const toast = useRef()
  const currentTimeInUserTimeZone = new Date()
  const { t } = useTranslation()
  const currentTime = moment(currentTimeInUserTimeZone).format('YYYY-MM-DDTHH:mm:ss')
  const userDetails = getLocalUserDetails()
  const { webImageSizeLimit, mobileImageSizeLimit, mobileImageResolutionWidth, mobileImageResolutionHeight, oneKB, videoMaxDurationMins } = IMAGE_UPLOAD_PARAMETERS

  const [selectedImages, setSelectedImages] = useState([])
  const [selectedFileForWeb, setSelectedFileForWeb] = useState([])
  const [selectedFileForMobile, setSelectedFileForMobile] = useState([])
  const [activeIndex, setActiveIndex] = useState(defaultImages?.length > 0 ? 0 : -1)
  const [uploadedFilesWeb, setUploadedFilesWeb] = useState([])
  const [uploadedFilesMobile, setUploadedFilesMobile] = useState([])
  const [submitButtonDisabled, setSubmitButtonDisabled] = useState(true)
  const [submittedWebImages, setSubmittedWebImages] = useState([])
  const [submittedMobileImages, setSubmittedMobileImages] = useState([])

  const thumbnailTemplate = (image) => <img src={image.imageUrl} alt={image.alt} className="thumbnail-image" />

  // useEffect to initialize state for testing purposes
  useEffect(() => {
    if (mockAddNewsFeedModal) {
      setUploadedFilesWeb(mockUploadedFilesWeb)
      setUploadedFilesMobile(mockUploadedFilesMobile)
    } else if (mockAddNewsFeedModalApiFlag) {
      handleMediaUpload(mockAddNewsFeedModalApiFlag, mockDeviceType)
    }
  }, [])

  const handleFileSelect = (event, device) => {
    event?.files?.forEach((ele) => {
      const allowedFormats = ['jpg', 'jpeg', 'png', 'webp', 'mp4']

      const selectedFileExtension = ele?.name?.split('.')?.pop()

      if (!allowedFormats.includes(selectedFileExtension)) {
        toast.current.show({ severity: t('ERROR_TOAST'), summary: t('INVALID_MEDIA_FORMAT'), detail: t('PLEASE_SELECT_VALID_MEDIA_FORMATS') })

        return
      }

      const selectedMedia = event.files && event.files[0]

      if (device === 'web') {
        if (selectedMedia && !uploadedFilesWeb.some((file) => file.name === selectedMedia.name)) {
          checkForFileSize('web', [...selectedFileForWeb, selectedMedia], webImageSizeLimit, 'mobile', selectedFileForMobile, mobileImageSizeLimit)
          setUploadedFilesWeb((prevUploadedFiles) => [...prevUploadedFiles, selectedMedia])
          setSelectedFileForWeb((prevSelectedFile) => [...prevSelectedFile, selectedMedia])
        }
      } else if (device === 'mobile') {
        if (selectedMedia.type.startsWith('image/')) {
          const img = new Image()

          img.src = URL.createObjectURL(selectedMedia)

          img.onload = () => {
            const width = img.width
            const height = img.height

            const isResolutionExceeded = !(width === mobileImageResolutionWidth && height === mobileImageResolutionHeight)

            processMediaUpload(selectedMedia, isResolutionExceeded, null)
          }
        } else if (selectedMedia.type.startsWith('video/')) {
          const video = document.createElement('video')

          video.src = URL.createObjectURL(selectedMedia)

          video.onloadedmetadata = () => {
            const duration = video.duration

            const isDurationExceeded = duration > videoMaxDurationMins * 60

            processMediaUpload(selectedMedia, false, isDurationExceeded)
          }
        }

        const processMediaUpload = (media, resolutionExceeded, durationExceeded) => {
          if (media && !uploadedFilesMobile.some((file) => file.selectedMedia.name === media.name)) {
            const updatedMobileObject = {
              selectedMedia: media,
              isResolutionExceeded: resolutionExceeded,
              isDurationExceeded: durationExceeded
            }

            checkForFileSize('mobile', [...selectedFileForMobile, updatedMobileObject], mobileImageSizeLimit, 'web', selectedFileForWeb, webImageSizeLimit)
            setUploadedFilesMobile((prevUploadedFiles) => [...prevUploadedFiles, updatedMobileObject])
            setSelectedFileForMobile((prevSelectedFile) => [...prevSelectedFile, updatedMobileObject])
          }
        }
      }
    })
  }

  const handleImageSubmit = async () => {
    closeModal()
    setLoading(true)
    const s3Images = []
    const promises = []

    // Default Image Submission
    const selectedIndexInNewsFeed = newsFeedMedia.filter((el) => el?.defaultImages === true).map((item) => item?.selectedIndex)
    const defaultIndexesRemoved = selectedIndexInNewsFeed.filter((index) => !selectedImages.includes(index))

    const selectedImageObjects = selectedImages.flatMap((selectedIndex) => {
      // Check if selectedIndex is already not present
      if (!selectedIndexInNewsFeed.includes(selectedIndex)) {
        const { imageUrl, mobileImageUrl, nameOfMedia, typeOfMedia, uploadedFileName, extensionOfMedia, sizeOfMedia } = defaultImages[selectedIndex]

        const currentTimeString = new Date(currentTime).toJSON().toString()

        const baseObject = {
          idNewsFeedMedia: 0,
          idNewsFeed: 0,
          createdOn: currentTimeString,
          createdBy: userDetails.id,
          updateOn: currentTimeString,
          updatedBy: 0,
          typeMedia: typeOfMedia,
          isActive: true,
          nameOfMedia: nameOfMedia,
          extensionOfMedia: extensionOfMedia,
          pathOfMedia: '',
          sizeOfMedia: sizeOfMedia,
          mediaData: nameOfMedia,
          uploadedFileName: uploadedFileName,
          defaultImages: true,
          selectedIndex
        }

        const objectForDesktop = { ...baseObject, imgflag: 1, webimgpath: imageUrl, mobimgpath: '' }
        const objectForMobile = { ...baseObject, imgflag: 2, webimgpath: '', mobimgpath: mobileImageUrl }

        // Return the objects for desktop and mobile
        return [objectForDesktop, objectForMobile]
      }

      return []
    })

    const createMediaObject = (file, url, isMobileMedia, index) => {
      const { name, size, type } = file
      const currentTimeString = new Date(currentTime).toJSON().toString()

      const baseObject = {
        idNewsFeedMedia: 0,
        idNewsFeed: 0,
        createdOn: currentTimeString,
        createdBy: userDetails.id,
        updatedOn: currentTimeString,
        updatedBy: 0,
        typeMedia: type.split('/')[0],
        isActive: true,
        extensionOfMedia: type.split('/')[1],
        sizeOfMedia: size,
        nameOfMedia: name,
        mediaData: type.split('/')[0],
        uploadedFileName: url.split('/')[3],
        defaultImages: false,
        selectedIndex: index,
        pathOfMedia: '',
        objectURL: file?.objectURL
      }

      return isMobileMedia ? { ...baseObject, webimgpath: '', mobimgpath: url, imgflag: 2 } : { ...baseObject, webimgpath: url, mobimgpath: '', imgflag: 1 }
    }

    // Image Upload to S3Bucket and Object Creation - WEB
    if (selectedFileForWeb?.length > 0) {
      setSubmittedWebImages([...selectedFileForWeb])
      selectedFileForWeb.forEach((file, index) => {
        promises.push(
          (async () => {
            const webUrl = await handleMediaUpload(file, 'web')
            const mediaObject = createMediaObject(file, webUrl, false, index)

            s3Images.push(mediaObject)
          })()
        )
      })
    }

    // Image Upload to S3Bucket and Object Creation - MOBILE
    if (selectedFileForMobile?.length > 0) {
      setSubmittedMobileImages([...selectedFileForMobile])
      selectedFileForMobile?.forEach((file, index) => {
        promises.push(
          (async () => {
            const mobileUrl = await handleMediaUpload(file.selectedMedia, 'mobile')
            const mediaObject = createMediaObject(file.selectedMedia, mobileUrl, true, index)

            s3Images.push(mediaObject)
          })()
        )
      })
    }

    await Promise.all(promises)

    const defaultImagesAfterRemoval = newsFeedMedia.filter((el) => !defaultIndexesRemoved.includes(el?.selectedIndex) && el?.defaultImages)

    const allImages = [...selectedImageObjects, ...defaultImagesAfterRemoval, ...s3Images]

    setLoading(false)
    setNewsFeedMedia(allImages)
  }

  const handleMediaUpload = async (file, deviceType) => {
    const fileData = new FormData()

    fileData.append('file', file)

    const uploadMethod = deviceType === 'web' ? apiAdapterCoffeeWeb.uploadFileToS3Bucket : apiAdapterCoffeeWeb.uploadFileToS3BucketMobile

    const { data } = await uploadMethod(fileData)

    return data
  }

  const removeImage = (index, nameOfMedia) => {
    setButtonDisabled(false)
    const updatedNewsFeedMedia = [...newsFeedMedia]
    const removedImage = updatedNewsFeedMedia[index]

    if (removedImage) {
      updatedNewsFeedMedia.splice(index, 1)
      setNewsFeedMedia(updatedNewsFeedMedia)

      if (removedImage.defaultImages) {
        setSelectedImages((prevSelectedIndices) => prevSelectedIndices.filter((selectedIndex, i) => i !== index && selectedIndex))
      } else {
        const updatedUploadedFilesWeb = uploadedFilesWeb.filter((file) => file.name !== nameOfMedia)
        const updatedUploadedFilesMobile = uploadedFilesMobile.filter((file) => file.selectedMedia.name !== nameOfMedia)
        const updatedSelectedFilesForWeb = selectedFileForWeb.filter((file) => file.name !== nameOfMedia)
        const updatedSelectedFilesForMobile = selectedFileForMobile.filter((file) => file.selectedMedia.name !== nameOfMedia)

        setUploadedFilesWeb(updatedUploadedFilesWeb)
        setUploadedFilesMobile(updatedUploadedFilesMobile)
        setSelectedFileForWeb(updatedSelectedFilesForWeb)
        setSelectedFileForMobile(updatedSelectedFilesForMobile)
        setSubmittedWebImages(updatedUploadedFilesWeb)
        setSubmittedMobileImages(updatedUploadedFilesMobile)
      }
    }
  }

  const handleImageClick = (index) => {
    const isSelected = selectedImages.includes(index)
    let updatedSelectedImages

    if (!isSelected) {
      updatedSelectedImages = [...selectedImages, index]
    } else {
      updatedSelectedImages = selectedImages.filter((selectedIndex) => selectedIndex !== index)
    }

    setSelectedImages(updatedSelectedImages)

    if (selectedFileForWeb?.length > 0 && selectedFileForMobile?.length > 0) {
      checkForFileSize('web', selectedFileForWeb, webImageSizeLimit, 'mobile', selectedFileForMobile, mobileImageSizeLimit)
    } else if (selectedFileForWeb?.length > 0 && !selectedFileForMobile?.length) {
      isFileSizeExceeded('web', selectedFileForWeb, webImageSizeLimit)
    } else if (selectedFileForMobile?.length > 0 && !selectedFileForWeb?.length > 0) {
      isFileSizeExceeded('mobile', selectedFileForMobile, mobileImageSizeLimit)
    } else if (!selectedFileForMobile?.length > 0 && !selectedFileForWeb?.length > 0) {
      setSubmitButtonDisabled(false)
    }
  }

  const handleCancelClick = (index, device) => {
    if (device === 'web') {
      const remainingUploadedImages = selectedFileForWeb.filter((_, ind) => ind !== index)

      setSelectedFileForWeb([...remainingUploadedImages])
      setUploadedFilesWeb([...remainingUploadedImages])

      checkForFileSize('web', remainingUploadedImages, webImageSizeLimit, 'mobile', selectedFileForMobile, mobileImageSizeLimit)
    } else if (device === 'mobile') {
      const remainingUploadedImages = selectedFileForMobile.filter((_, ind) => ind !== index)

      setSelectedFileForMobile([...remainingUploadedImages])
      setUploadedFilesMobile([...remainingUploadedImages])
      checkForFileSize('mobile', remainingUploadedImages, mobileImageSizeLimit, 'web', selectedFileForWeb, webImageSizeLimit)
    }
  }

  const checkForFileSize = (primaryDevice, primaryFile, primaryFileSize, secondaryDevice, secondaryFile, secondaryFileSize) => {
    isFileSizeExceeded(primaryDevice, [...primaryFile], primaryFileSize)
    if (secondaryFile?.length > 0) {
      isFileSizeExceeded(secondaryDevice, [...secondaryFile], secondaryFileSize)
    }
  }

  let isWebSizeExceeded = false
  let isMobileSizeExceeded = false

  const isFileSizeExceeded = (deviceType, uploadedFile, fileSize) => {
    if (deviceType === 'web') {
      isWebSizeExceeded = uploadedFile.some((file) => file.type.startsWith('image/') && Math.floor(file.size / (oneKB * oneKB)) > fileSize)
    }

    if (deviceType === 'mobile') {
      isMobileSizeExceeded = uploadedFile.some((file) => file.selectedMedia.type.startsWith('image/') && Math.floor(file.selectedMedia.size / oneKB) > fileSize)
    }

    setSubmitButtonDisabled(isWebSizeExceeded || isMobileSizeExceeded)
  }

  const isImageButtonDisabled = submitButtonDisabled || (selectedFileForMobile?.length > 0 ? selectedFileForMobile.some((file) => file?.isDurationExceeded || file?.isResolutionExceeded) : false)

  const handleDefaultCustomRender = () => {
    setRenderCustomUploader((prev) => !prev)
  }

  const handleModalClose = () => {
    closeModal()

    setUploadedFilesMobile([...submittedMobileImages])
    setUploadedFilesWeb([...submittedWebImages])
    setSelectedFileForWeb([...submittedWebImages])
    setSelectedFileForMobile([...submittedMobileImages])
    setSelectedImages(newsFeedMedia.filter((el) => el?.defaultImages).map((el) => el?.selectedIndex))
  }

  const MessageTemplate = ({ imageWidth, imageHeight, videoLimit, webImageLimit, mobileImageLimit }) => (
    <div className="media-constraints">
      <ul>
        <li>
          Image Resolution: {imageWidth}px × {imageHeight}px
        </li>
        <li>Image File Size: Upto {webImageLimit ? `${webImageLimit}MB` : `${mobileImageLimit}KB`}</li>
        <li>Video Duration: {videoLimit ? `Upto ${videoLimit} Mins` : 'No Restriction'}</li>
      </ul>
    </div>
  )

  return (
    <>
      {newsFeedMedia?.length > 0 && (
        <>
          <div className="thumbnail-image-heading">
            <div className="image-heading">{t('WEB_IMAGES')}</div>
            <div className="image-heading">{t('MOBILE_IMAGES')}</div>
          </div>
          <div className="image-display">
            <div className="web-images-display">
              {newsFeedMedia.map((ele, index) => (
                <React.Fragment key={index}>
                  {(ele?.webimgpath || ele?.pathofmedia) && (
                    <div className="added-image">
                      <div className="uploaded-image-close-button">
                        <Button icon="pi pi-times" className="p-button-outlined p-button-secondary close-button" onClick={() => removeImage(index, ele.nameOfMedia)} data-testid="close-button" />
                      </div>
                      <div className="image-size">{(ele.typeMedia === 'image' || ele.typeMedia === 'video') && ele.typeMedia === 'video' ? <video src={ele?.webimgpath} type={'video/mp4'} autoPlay={false} controls disablePictureInPicture controlsList="nodownload noplaybackrate" /> : <img src={ele?.webimgpath} alt="thumbnail" />}</div>
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
            <div className="mobile-images-display">
              {newsFeedMedia.map((ele, index) => (
                <React.Fragment key={index}>
                  {ele?.mobimgpath && (
                    <div className="added-image">
                      <div className="uploaded-image-close-button">
                        <Button icon="pi pi-times" className="p-button-outlined p-button-secondary close-button" onClick={() => removeImage(index, ele.nameOfMedia)} data-testid="close-button" />
                      </div>
                      {ele?.mobimgpath && <div className="image-size">{(ele.typeMedia === 'image' || ele.typeMedia === 'video') && ele.typeMedia === 'video' ? <video src={ele?.mobimgpath} type={'video/mp4'} autoPlay={false} controls disablePictureInPicture controlsList="nodownload noplaybackrate" /> : <img src={ele?.mobimgpath} alt="thumbnail" />}</div>}
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </>
      )}
      <Dialog visible={isModalOpen} onHide={() => handleModalClose()} header={<div className="modal-main-header">{t('ADD_THUMBNAIL_IMG')}</div>} className="dialog-image-component" data-testid="add-news-dialog">
        <div className="thumbnail-modal">
          {renderCustomUploader && (
            <>
              <div className="default-image-handler" onClick={() => handleDefaultCustomRender()}>
                {t('ADD_DEFAULT_IMAGES')}
              </div>
            </>
          )}
          {!renderCustomUploader && (
            <>
              <div className="section-header">
                <span className="section-header-text">{t('SELECT_FROM_BELOW_IMAGES')}</span>
              </div>
              {defaultImages?.length > 0 && (
                <div className="card flex justify-content-center" data-testid="image-gallery">
                  <Galleria key={galleria} value={defaultImages} activeIndex={activeIndex} onItemChange={(e) => setActiveIndex(e.index)} showThumbnails={false} thumbnail={thumbnailTemplate} />
                  <div className="grid">
                    {defaultImages.map((image, index) => (
                      <div key={index} className={`default-img ${selectedImages.includes(index) ? 'selected-image' : 'default-image'}`} onClick={() => handleImageClick(index)}>
                        <img src={image.imageUrl} alt="default" />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          <div className="or-text">{t('OR')}</div>
          {!renderCustomUploader && (
            <div className="custom-image-handler" onClick={() => handleDefaultCustomRender()}>
              {t('ADD_CUSTOM_IMAGES')}
            </div>
          )}
          {renderCustomUploader && (
            <div className="file-upload-wrapper">
              <div className="second-half-container">
                <div className="file-upload-header">
                  <span className="file-upload-text">{t('UPLOAD_FOR_WEB')}</span>
                  <MessageTemplate imageWidth={mobileImageResolutionWidth} imageHeight={mobileImageResolutionHeight} webImageLimit={webImageSizeLimit} />
                </div>
                <FileUpload
                  className="choose-file-button"
                  chooseLabel="Choose Files"
                  accept=".jpg,.jpeg,.png,.webp,.mp4"
                  data-testid="web-file-upload-button"
                  auto={true}
                  multiple={true}
                  onSelect={(event) => handleFileSelect(event, 'web')}
                  emptyTemplate={
                    <div>
                      {uploadedFilesWeb.length > 0 ? (
                        uploadedFilesWeb.map((file, index) => {
                          const content = (
                            <div key={index} className="add-file-upload">
                              <div className="image-file">
                                <div className="add-image-container" data-testid="web-images-container">
                                  <div className="add-image-container">{file.type.startsWith('image/') ? <img src={URL.createObjectURL(file)} alt={`uploaded-file-${index}`} /> : <video src={URL.createObjectURL(file)} type={file.type} autoPlay={false} controls disablePictureInPicture controlsList="nodownload noplaybackrate" />}</div>
                                </div>
                                <div className="cancel-action" data-testid="web-cancel-button">
                                  <Button type="button" icon="pi pi-times" className="p-button-outlined p-button-rounded p-button-danger ml-auto" onClick={() => handleCancelClick(index, 'web')} />
                                </div>
                              </div>
                              {file?.type.startsWith('image/') && Math.floor(file.size / (oneKB * oneKB)) > webImageSizeLimit && <div className="size-error-message">{t('WEB_IMAGE_SIZE_LIMIT', { webImageSizeLimit })}</div>}
                            </div>
                          )

                          return content
                        })
                      ) : (
                        <div className="drag-drop">
                          <p className="choose-title">{t('DRAG_AND_DROP_IMAGES')}</p>
                        </div>
                      )}
                    </div>
                  }
                />
              </div>
              <div className="second-half-container">
                <div className="file-upload-header">
                  <span className="file-upload-text">{t('UPLOAD_FOR_MOBILE')}</span>
                  <MessageTemplate imageWidth={mobileImageResolutionWidth} imageHeight={mobileImageResolutionHeight} mobileImageLimit={mobileImageSizeLimit} videoLimit={videoMaxDurationMins} />
                </div>
                <FileUpload
                  className="choose-file-button"
                  chooseLabel="Choose Files"
                  accept=".jpg,.jpeg,.png,.webp,.mp4"
                  data-testid="mobile-file-upload-button"
                  auto={true}
                  multiple={true}
                  onSelect={(event) => handleFileSelect(event, 'mobile')}
                  emptyTemplate={
                    <div>
                      {uploadedFilesMobile.length > 0 ? (
                        uploadedFilesMobile.map((file, index) => {
                          const content = (
                            <div key={index} className="add-file-upload">
                              <div className="image-file">
                                <div className="add-image-container" data-testid="mobile-images-container">
                                  <div className="add-image-container">{file?.selectedMedia.type.startsWith('image/') ? <img src={URL.createObjectURL(file?.selectedMedia)} alt={`uploaded-file-${index}`} /> : <video src={URL.createObjectURL(file?.selectedMedia)} type={file?.selectedMedia.type} autoPlay={false} controls disablePictureInPicture controlsList="nodownload noplaybackrate" />}</div>
                                </div>
                                <div className="cancel-action" data-testid="mobile-cancel-button">
                                  <Button type="button" icon="pi pi-times" className="p-button-outlined p-button-rounded p-button-danger ml-auto" onClick={() => handleCancelClick(index, 'mobile')} />
                                </div>
                              </div>
                              {file?.isResolutionExceeded && <div className="size-error-message">{t('MOBILE_IMAGE_RESOLUTION_LIMIT', { mobileImageResolutionWidth, mobileImageResolutionHeight })}</div>}
                              {file?.isDurationExceeded && <div className="size-error-message">{t('VIDEO_DURATION_LIMIT', { videoMaxDurationMins })}</div>}
                              {file?.selectedMedia.type.startsWith('image/') && Math.floor(file.selectedMedia.size / oneKB) > mobileImageSizeLimit && <div className="size-error-message">{t('IMAGE_SIZE_LIMIT', { mobileImageSizeLimit })}</div>}
                            </div>
                          )

                          return content
                        })
                      ) : (
                        <div className="drag-drop">
                          <p className="choose-title">{t('DRAG_AND_DROP_IMAGES')}</p>
                        </div>
                      )}
                    </div>
                  }
                />
              </div>
            </div>
          )}
        </div>
        <div className="default-button-container">
          <ButtonComponent type="submit" onClick={handleImageSubmit} disabled={isImageButtonDisabled} />
        </div>
      </Dialog>
      <Toast ref={toast} position="top-right" />
    </>
  )
}

export default Loader(ModalComponent)
